import React, {
  useCallback,
  useState,
  useMemo,
} from 'react';
import format from 'string-template';
import PropTypes from 'prop-types';
import moment from 'moment';

import { formatCurrency } from '../../../../../utils/formatters';
import { DateFormat } from '../../../../../utils/date';
import { DURATION_UNIT, toMomentUnit } from '../../../../../utils/durationUnit';
import GenericErrorMessage from '../../../../../components/GenericErrorMessage';
import colors from '../../../../../styles/colors';
import SubmitButton from '../SubmitButton';

import {
  Container,
  CheckoutContent,
  ErrorMessage,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
  InputFieldGroup,
  ElementContainer,
  Label,
  BottomTextContainer,
  StyledLockIcon,
  DoubleElementContainer,
  PaymentSummary,
  PaymentSummaryTitle,
  PaymentSummaryContent,
  PaymentContentContainer,
  PaymentSummarySubContent,
  TextInput,
  CountrySelect,
} from './styles';

import texts from './texts.json';
import COUNTRIES from './countries';

const PaymentForm = ({
  isLoading,
  errorMessage,
  onSubmit,
  isDarkTheme,
  color,
  contract,
  isApplePayView,
  automaticTaxEnabled,
  billingAddress,
  onBillingAddressChange,
}) => {
  // Stripe field errors
  const [fieldErrors, setFieldErrors] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
  });
  const [isFieldEmpty, setIsFieldEmpty] = useState({
    cardNumber: true,
    cardExpiry: true,
    cardCvc: true,
  });

  const {
    initialPayment,
    upfrontPayment,
    currency,
    startDate,
    initialTerm,
    durationUnit,
  } = contract;

  // Challenge contracts count their term in weeks; everything else in months.
  const termMomentUnit = toMomentUnit(durationUnit);

  const submitButtonText = useMemo(() => {
    if (moment(startDate, DateFormat.DEFAULT_DATE_FORMAT).isAfter(moment()) && Number(upfrontPayment) === 0) {
      return texts.checkout;
    }
    return format(texts.payAmount, {
      amount: formatCurrency(upfrontPayment || initialPayment, currency),
    });
  }, [
    startDate,
    upfrontPayment,
    initialPayment,
    currency,
  ]);

  const stripeElementStyle = {
    base: {
      iconColor: isDarkTheme ? colors.base.alpha : colors.base.beta,
      color: isDarkTheme ? colors.base.alpha : colors.base.beta,
    },
  };

  const handleStripeFieldChange = useCallback((event) => {
    const { error, elementType, empty } = event;
    if (error) {
      setFieldErrors((prevErrors) => ({ ...prevErrors, [elementType]: error.message }));
    } else {
      setFieldErrors((prevErrors) => ({ ...prevErrors, [elementType]: '' }));
    }
    setIsFieldEmpty((prevState) => ({ ...prevState, [elementType]: empty }));
  }, []);

  const areFieldErrors = useMemo(() => (
    Object.values(fieldErrors).some((field) => !!field)
  ), [fieldErrors]);

  const handleAddressChange = useCallback((field, value) => {
    if (onBillingAddressChange) {
      onBillingAddressChange({ ...billingAddress, [field]: value });
    }
  }, [billingAddress, onBillingAddressChange]);

  const isAddressComplete = useMemo(() => (
    !automaticTaxEnabled || (
      !!billingAddress.line1
      && !!billingAddress.city
      && !!billingAddress.postal_code
      && !!billingAddress.country
    )
  ), [automaticTaxEnabled, billingAddress]);

  return (
    <Container>
      <CheckoutContent>
        {!isApplePayView && (
          <>
            <InputFieldGroup>
              <ElementContainer $isFieldEmpty={isFieldEmpty.cardNumber} $isDarkTheme={isDarkTheme}>
                <Label $isDarkTheme={isDarkTheme}>{texts.cardNumber}</Label>
                <StripeCardNumberElement
                  options={{
                    showIcon: true,
                    style: stripeElementStyle,
                  }}
                  onChange={handleStripeFieldChange}
                  $isDarkTheme={isDarkTheme}
                />
              </ElementContainer>
              <DoubleElementContainer>
                <ElementContainer
                  $isFieldEmpty={isFieldEmpty.cardExpiry}
                  $isDarkTheme={isDarkTheme}
                >
                  <Label $isDarkTheme={isDarkTheme}>{texts.expiryDate}</Label>
                  <StripeCardExpiryElement
                    onChange={handleStripeFieldChange}
                    $isDarkTheme={isDarkTheme}
                    options={{
                      style: stripeElementStyle,
                    }}
                  />
                </ElementContainer>
                <ElementContainer
                  $isFieldEmpty={isFieldEmpty.cardCvc}
                  $isDarkTheme={isDarkTheme}
                >
                  <Label $isDarkTheme={isDarkTheme}>{texts.cvc}</Label>
                  <StripeCardCvcElement
                    options={{
                      style: stripeElementStyle,
                    }}
                    onChange={handleStripeFieldChange}
                    $isDarkTheme={isDarkTheme}
                  />
                </ElementContainer>
              </DoubleElementContainer>
            </InputFieldGroup>
            {fieldErrors.cardNumber && <ErrorMessage>{fieldErrors.cardNumber}</ErrorMessage>}
            {fieldErrors.cardExpiry && <ErrorMessage>{fieldErrors.cardExpiry}</ErrorMessage>}
            {fieldErrors.cardCvc && <ErrorMessage>{fieldErrors.cardCvc}</ErrorMessage>}
          </>
        )}
        {!isApplePayView && automaticTaxEnabled && (
          <InputFieldGroup>
            <ElementContainer $isDarkTheme={isDarkTheme} $isFieldEmpty={!billingAddress.line1}>
              <Label $isDarkTheme={isDarkTheme}>{texts.addressLine1}</Label>
              <TextInput
                $isDarkTheme={isDarkTheme}
                value={billingAddress.line1}
                onChange={(e) => handleAddressChange('line1', e.target.value)}
                placeholder={texts.addressLine1Placeholder}
              />
            </ElementContainer>
            <ElementContainer $isDarkTheme={isDarkTheme} $isFieldEmpty={!billingAddress.city}>
              <Label $isDarkTheme={isDarkTheme}>{texts.city}</Label>
              <TextInput
                $isDarkTheme={isDarkTheme}
                value={billingAddress.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                required
              />
            </ElementContainer>
            <DoubleElementContainer>
              <ElementContainer $isDarkTheme={isDarkTheme} $isFieldEmpty={!billingAddress.postal_code}>
                <Label $isDarkTheme={isDarkTheme}>{texts.postalCode}</Label>
                <TextInput
                  $isDarkTheme={isDarkTheme}
                  value={billingAddress.postal_code}
                  onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                  required
                />
              </ElementContainer>
              <ElementContainer $isDarkTheme={isDarkTheme} $isFieldEmpty={!billingAddress.country}>
                <Label $isDarkTheme={isDarkTheme} htmlFor="billing-country">{texts.country}</Label>
                <CountrySelect
                  id="billing-country"
                  $isDarkTheme={isDarkTheme}
                  $isEmpty={!billingAddress.country}
                  value={billingAddress.country}
                  onChange={(e) => handleAddressChange('country', e.target.value)}
                  required
                >
                  <option value="">{' '}</option>
                  {COUNTRIES.map(({ code, name }) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </CountrySelect>
              </ElementContainer>
            </DoubleElementContainer>
          </InputFieldGroup>
        )}
        <PaymentSummary>
          <PaymentSummaryTitle $isDarkTheme={isDarkTheme}>{texts.paymentSummary}</PaymentSummaryTitle>
          {upfrontPayment > 0 && (
            <PaymentContentContainer>
              <PaymentSummaryContent $isDarkTheme={isDarkTheme}>
                <div>{texts.advancePayment}</div>
                <div>{formatCurrency(upfrontPayment, currency)}</div>
              </PaymentSummaryContent>
              <PaymentSummarySubContent $color={colors.base.danger}>
                {texts.dueToday}
              </PaymentSummarySubContent>
            </PaymentContentContainer>
          )}
          <PaymentContentContainer>
            <PaymentSummaryContent $isDarkTheme={isDarkTheme}>
              <div>
                {format(texts.firstPayment, {
                  ...(upfrontPayment > 0 && { balancePayment: texts.balanceOf }),
                  ...(initialTerm > 1 && { count: initialTerm }),
                  termUnit: durationUnit === DURATION_UNIT.WEEK ? texts.termUnits.week : texts.termUnits.month,
                })}
              </div>
              <div>
                {formatCurrency(
                  upfrontPayment ? initialPayment - upfrontPayment : initialPayment,
                  currency,
                )}
              </div>
            </PaymentSummaryContent>
            <PaymentSummarySubContent $color={upfrontPayment ? colors.shades.secondary11 : colors.shades.gamma14}>
              {upfrontPayment ? format(texts.billedOnDate, {
                startDate: moment(startDate).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
              }) : format(texts.paymentPeriod, {
                startDate: moment(startDate).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
                endDate: moment(startDate).add(
                  contract.initialTerm, termMomentUnit,
                ).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
              })}
            </PaymentSummarySubContent>
          </PaymentContentContainer>
        </PaymentSummary>
        {!isApplePayView && (
          <>
            <SubmitButton
              isLoading={isLoading}
              disabled={areFieldErrors || !isAddressComplete}
              onClick={onSubmit}
              $isDarkTheme={isDarkTheme}
              $background={color}
            >
              {submitButtonText}
            </SubmitButton>
            <BottomTextContainer $isDarkTheme={isDarkTheme}>
              <StyledLockIcon />
              {texts.endToEndEncrypted}
            </BottomTextContainer>
            {
              errorMessage && <GenericErrorMessage>{errorMessage}</GenericErrorMessage>
            }
          </>
        )}
      </CheckoutContent>
    </Container>
  );
};

PaymentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  color: PropTypes.string,
  errorMessage: PropTypes.string,
  isDarkTheme: PropTypes.bool.isRequired,
  contract: PropTypes.object.isRequired,
  isApplePayView: PropTypes.bool.isRequired,
  automaticTaxEnabled: PropTypes.bool,
  billingAddress: PropTypes.shape({
    line1: PropTypes.string,
    city: PropTypes.string,
    postal_code: PropTypes.string,
    country: PropTypes.string,
  }),
  onBillingAddressChange: PropTypes.func,
};

PaymentForm.defaultProps = {
  errorMessage: null,
  color: '',
  automaticTaxEnabled: false,
  billingAddress: {
    line1: '',
    city: '',
    postal_code: '',
    country: '',
  },
  onBillingAddressChange: null,
};

export default PaymentForm;
